import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { HeartPulse, ShieldCheck, ChevronRight } from "lucide-react";

const CLINIC_BG = "https://images.pexels.com/photos/16571735/pexels-photo-16571735.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0F172A] relative z-10">
      <div className="hidden lg:block relative overflow-hidden">
        <img src={CLINIC_BG} alt="Youngness Centre clinic" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/70 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <div className="flex items-center gap-2 text-[#C9A24B] mb-4">
            <HeartPulse className="h-6 w-6" />
            <span className="tracking-[0.3em] text-xs uppercase text-slate-300">Youngness Centre</span>
          </div>
          <h2 className="font-display text-4xl text-white leading-tight font-light">
            Own every patient's<br />experience, end to end.
          </h2>
          <p className="text-slate-400 mt-4 max-w-md leading-relaxed">
            Enquiry to 90-day follow-up — feedback, concerns and dues, all in one place. Nothing slips through.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md ync-fade-up">
          <div className="flex items-center gap-2 text-[#C9A24B] mb-2">
            <HeartPulse className="h-5 w-5" />
            <span className="tracking-[0.3em] text-xs uppercase">by Dr. Awish</span>
          </div>
          <h1 className="font-display text-4xl text-white font-light mb-3">Relationship Manager CRM</h1>
          <p className="text-slate-400 mb-10 leading-relaxed">
            Sign in to manage patient journeys, follow-ups, feedback, concerns and pending dues across all branches.
          </p>

          <Button
            data-testid="google-signin-button"
            onClick={handleLogin}
            className="w-full h-12 bg-white text-[#0F172A] hover:bg-slate-100 font-semibold text-base rounded-lg"
          >
            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-2 mt-8 text-slate-500 text-xs">
            <ShieldCheck className="h-4 w-4 text-[#059669]" />
            Secure OAuth sign-in · Same access as other Youngness CRMs
          </div>
          <div className="mt-10 pt-6 border-t border-white/5 text-slate-500 text-sm flex items-center gap-1">
            New Delhi (Sarita Vihar) · Mathura · Jaipur <ChevronRight className="h-3 w-3" />
          </div>
        </div>
      </div>
    </div>
  );
}
